import { z } from 'zod'

import type { CustomersListParams } from '@gta/api-client'

/**
 * 客户域 URL 状态契约(唯一真源)。
 *
 * 红线:筛选 / 排序 / 分页 / tab 一律进 search params ——
 * 冷加载任意一条 /customers?... 或 /customers/$id?tab=... 都必须完整还原页面状态,
 * 组件内不得用 useState 存这些状态。
 *
 * 取值面严格对齐后端 `GET /api/v1/customers` 的手写 query params
 * (gta/apps/crm_apps/crm_core/api/views.py::CustomerListView):
 *   search / channel / customer_type / created_after / created_before / ordering / page / page_size
 * 非法值一律 `.catch()` 降级成默认值,保证脏 URL 不白屏(后端对非法枚举是 400)。
 */

/** 后端 CUSTOMER_ORDERING_FIELDS 白名单的正反序全展开。 */
export const CUSTOMER_ORDERING_VALUES = [
  '-create_time',
  'create_time',
  '-update_time',
  'update_time',
  'name',
  '-name',
  'customer_number',
  '-customer_number',
] as const

export type CustomerOrdering = (typeof CUSTOMER_ORDERING_VALUES)[number]

/** 可点排序的列(与上面白名单的字段名一致)。 */
export type CustomerSortField = 'create_time' | 'update_time' | 'name' | 'customer_number'

export const DEFAULT_CUSTOMER_ORDERING: CustomerOrdering = '-create_time'
export const DEFAULT_PAGE = 1
export const DEFAULT_PAGE_SIZE = 20

/** 后端 DemoPageNumberPagination.max_page_size = 100。 */
export const MAX_PAGE_SIZE = 100
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const

export const CUSTOMER_TYPE_VALUES = ['individual', 'company'] as const
export type CustomerTypeValue = (typeof CUSTOMER_TYPE_VALUES)[number]

/** 空串 / 全空白 = 未筛选(而不是「筛选空值」),统一降级成 undefined 以便从 URL 里消失。 */
const optionalText = z.string().trim().min(1).optional().catch(undefined)

/**
 * 后端 `parse_date` 对「格式不对」和「月/日越界」都是 400,所以这里两道都验:
 * 先卡 YYYY-MM-DD 形状,再回环一次真实日历(挡掉 2026-13-99 / 2026-02-31 这类)。
 */
function isRealCalendarDate(value: string): boolean {
  const date = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
}

const optionalIsoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(isRealCalendarDate)
  .optional()
  .catch(undefined)

export const customersSearchSchema = z.object({
  /** 模糊搜索:客户名 / 客户资源号 / 外部身份 ID(输入框 300ms 防抖后写入)。 */
  search: optionalText,
  /** 首触渠道 code(CustomerChannel.code,取值面来自 /meta/enums.channels)。 */
  channel: optionalText,
  customer_type: z.enum(CUSTOMER_TYPE_VALUES).optional().catch(undefined),
  created_after: optionalIsoDate,
  created_before: optionalIsoDate,
  // `.default()` 让这几项在**输入**侧可省略(Link/navigate 不必每次带全套 search),
  // `.catch()` 兜住脏值;两者叠加 = 「URL 里没有就用默认,有但不合法也用默认」。
  ordering: z.enum(CUSTOMER_ORDERING_VALUES).default(DEFAULT_CUSTOMER_ORDERING).catch(DEFAULT_CUSTOMER_ORDERING),
  page: z.coerce.number().int().min(1).default(DEFAULT_PAGE).catch(DEFAULT_PAGE),
  page_size: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE).catch(DEFAULT_PAGE_SIZE),
})

export type CustomersSearch = z.infer<typeof customersSearchSchema>

/** 等于默认值的参数不进 URL(stripSearchParams),保证 /customers 裸地址就是干净首屏。 */
export const CUSTOMERS_SEARCH_DEFAULTS = {
  ordering: DEFAULT_CUSTOMER_ORDERING,
  page: DEFAULT_PAGE,
  page_size: DEFAULT_PAGE_SIZE,
} satisfies Partial<CustomersSearch>

/**
 * 详情页的关系表用 tab 切,tab 态同样进 URL。
 *
 * ★W15(R3-B)加第三档 `won_receipts`。⚠️ 三档的**门控口径不同**,别当成同类:
 * `orders` 受 `modules` 清单门控(online 档契约里根本没有 `orders` 字段,PT-17 修的就是它);
 * `leads` 与 `won_receipts` **两 profile 恒在** —— 都是 `crm_core` 实体、落在基类上。
 */
export const CUSTOMER_DETAIL_TABS = ['leads', 'orders', 'won_receipts'] as const
export type CustomerDetailTab = (typeof CUSTOMER_DETAIL_TABS)[number]

export const DEFAULT_CUSTOMER_DETAIL_TAB: CustomerDetailTab = 'leads'

export const customerDetailSearchSchema = z.object({
  tab: z.enum(CUSTOMER_DETAIL_TABS).default(DEFAULT_CUSTOMER_DETAIL_TAB).catch(DEFAULT_CUSTOMER_DETAIL_TAB),
})

export type CustomerDetailSearch = z.infer<typeof customerDetailSearchSchema>

export const CUSTOMER_DETAIL_SEARCH_DEFAULTS = {
  tab: DEFAULT_CUSTOMER_DETAIL_TAB,
} satisfies Partial<CustomerDetailSearch>

/**
 * URL search params → 后端 query params。
 * 未设置的筛选项整个键都不下发(而不是下发 undefined),
 * 这样 react-query 的 queryKey 才不会因为「有键但值是 undefined」多出一份缓存。
 */
export function toCustomersListParams(search: CustomersSearch): CustomersListParams {
  return {
    page: search.page,
    page_size: search.page_size,
    ordering: search.ordering,
    ...(search.search === undefined ? {} : { search: search.search }),
    ...(search.channel === undefined ? {} : { channel: search.channel }),
    ...(search.customer_type === undefined ? {} : { customer_type: search.customer_type }),
    ...(search.created_after === undefined ? {} : { created_after: search.created_after }),
    ...(search.created_before === undefined ? {} : { created_before: search.created_before }),
  }
}

/** 是否处于「有筛选条件」状态(决定要不要显示重置按钮 / 空态文案怎么写)。 */
export function hasActiveFilters(search: CustomersSearch): boolean {
  return (
    search.search !== undefined ||
    search.channel !== undefined ||
    search.customer_type !== undefined ||
    search.created_after !== undefined ||
    search.created_before !== undefined
  )
}

/** 点表头排序:同字段则反转方向,换字段则默认降序(时间/数字)或升序(名称)。 */
export function nextOrdering(current: CustomerOrdering, field: CustomerSortField): CustomerOrdering {
  if (current === field) return `-${field}` as CustomerOrdering
  if (current === `-${field}`) return field as CustomerOrdering
  return (field === 'name' ? field : `-${field}`) as CustomerOrdering
}

/** 当前排序方向;该字段没在排序中则返回 null。 */
export function sortDirection(current: CustomerOrdering, field: CustomerSortField): 'asc' | 'desc' | null {
  if (current === field) return 'asc'
  if (current === `-${field}`) return 'desc'
  return null
}
